import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import gql from "graphql-tag"
import { useQuery } from "react-apollo"

import FeaturedIdeas from "../featured-ideas/featured-ideas"
import QuestionOfTheDay from "../question-of-the-day/question-of-the-day"
import convertProfilePicUrl from "../../utils/convert-profile-pic-url"
import { event } from "../../lib/gtag"
import { SKILL_PHRASES, skillById } from "../../data/skill-phrases"
import { SAMPLE_PLANS } from "../../data/sample-plans"

import "./visitor-home.scss"

// Premium members are the ones the grid lists (onboarding plan, D1), so the
// spotlight only ever shows someone who is already public there.
const FOUNDERS_WHO_BRING = gql`
  query HOME_FOUNDERS_WHO_BRING($where: String) {
    premiumUsersLookingForCofounder(skip: 0, perPagePremium: 12, where: $where) {
      id
      firstName
      profilePic
      location
      whyMakeGoodCofounder
      elevatorPitchForStartup
      hasSkills {
        itemId
      }
      wantsSkills {
        itemId
      }
    }
  }
`

const DEFAULT_BRING = 4 // business development → developer is the most common pair
const DEFAULT_NEED = 1

const plain = (s) =>
  String(s || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/[*_#>`~]/g, "")
    .replace(/\s+/g, " ")
    .trim()

const blurb = (u) => plain(u.whyMakeGoodCofounder) || plain(u.elevatorPitchForStartup)

const ROTATE_MS = 7000
const MAX_SHOWN = 6

// People with a photo and a few words, the ones looking for what you bring
// first. The query only returns Premium members, so everyone here is one.
const pickFounders = (list, bringId) => {
  const good = list.filter((u) => u.profilePic && blurb(u).length >= 40)
  const wantsYou = (u) => (u.wantsSkills || []).some((s) => s.itemId === bringId)
  const ranked = [...good.filter(wantsYou), ...good.filter((u) => !wantsYou(u))]
  return (ranked.length ? ranked : list.slice(0, 1)).slice(0, MAX_SHOWN)
}

const whereBringing = (itemId) =>
  JSON.stringify({ OR: [{ hasSkills_some: { OR: [{ itemId }] } }] })

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12 5 5L20 7" />
  </svg>
)

const SkillSelect = ({ id, value, onChange, phrase, tone }) => (
  <select
    id={id}
    className={`visitor-hero__select visitor-hero__select--${tone}`}
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
  >
    {SKILL_PHRASES.map((s) => (
      <option key={s.itemId} value={s.itemId}>
        {s[phrase]}
      </option>
    ))}
  </select>
)

// The product, not a claim: an example week, following the visitor's "need".
const PlanCard = ({ plan }) => (
  <figure className="plan-card" aria-label="An example FounderAssist plan">
    <div className="plan-card__head">
      <span className="plan-card__title">This week&rsquo;s plan</span>
      <span className="plan-card__tag">Example</span>
    </div>
    <p className="plan-card__goal">{plan.goal}</p>
    <ul className="plan-card__tasks">
      <li className="plan-card__task plan-card__task--done">
        <span className="plan-card__box">
          <IconCheck />
        </span>
        <span>{plan.done}</span>
      </li>
      {plan.next.map((task) => (
        <li key={task} className="plan-card__task">
          <span className="plan-card__box" />
          <span>{task}</span>
        </li>
      ))}
    </ul>
    <figcaption className="plan-card__foot">
      FounderAssist updates the plan each time you refresh it.
    </figcaption>
  </figure>
)

const PlanPreview = ({ plan, href, onStart }) => (
  <section className="plan-preview" aria-labelledby="plan-preview-title">
    <div className="plan-preview__copy">
      <h2 id="plan-preview-title" className="plan-preview__title">
        Your first week&rsquo;s plan
      </h2>
      <p className="plan-preview__text">
        Sign up and FounderAssist writes a plan for your first week, built around
        what you bring and who you need.
      </p>
      <Link href={href}>
        <a className="plan-preview__cta" onClick={() => onStart("preview")}>
          Sign up to see yours
        </a>
      </Link>
    </div>
    <ol className="plan-preview__tasks" aria-hidden="true">
      {[plan.next[0], ...plan.later].map((task) => (
        <li key={task} className="plan-preview__task">
          <span className="plan-preview__box" />
          <span className="plan-preview__blur">{task}</span>
        </li>
      ))}
    </ol>
  </section>
)

const FounderCard = ({ founder, bringId, needId }) => {
  const brings = (founder.hasSkills || []).map((s) => skillById(s.itemId)).filter(Boolean)
  const wants = (founder.wantsSkills || []).map((s) => skillById(s.itemId)).filter(Boolean)
  const wantsYou = wants.some((s) => s.itemId === bringId)
  const shownBring = brings.find((s) => s.itemId === needId) || brings[0]
  const shownWant = wantsYou ? skillById(bringId) : wants[0]
  return (
    <article className="spotlight-card">
      <div className="spotlight-card__head">
        <img
          className="spotlight-card__avatar"
          src={convertProfilePicUrl(founder.profilePic, 64, 64)}
          alt=""
          width="64"
          height="64"
        />
        <div className="spotlight-card__who">
          <h3 className="spotlight-card__name">{founder.firstName}</h3>
          {founder.location && <p className="spotlight-card__place">{founder.location}</p>}
        </div>
        <span className="spotlight-card__badge">Premium member</span>
      </div>
      <div className="spotlight-card__skills">
        {shownBring && (
          <div className="spotlight-card__skill-col">
            <span className="spotlight-card__skill-label">Brings</span>
            <span className="spotlight-card__pill spotlight-card__pill--blue">{shownBring.label}</span>
          </div>
        )}
        {shownWant && (
          <div className="spotlight-card__skill-col">
            <span className="spotlight-card__skill-label">Looking for</span>
            <span className="spotlight-card__pill">{shownWant.label}</span>
          </div>
        )}
      </div>
      <p className="spotlight-card__blurb">{blurb(founder)}</p>
      <Link href="/profile/[id]" as={`/profile/${founder.id}`}>
        <a className="spotlight-card__cta">See {founder.firstName}&rsquo;s profile</a>
      </Link>
    </article>
  )
}

const IconArrow = ({ flip }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={flip ? { transform: "scaleX(-1)" } : undefined}>
    <path d="m9 6 6 6-6 6" />
  </svg>
)

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches

const FounderSpotlight = ({ bring, need, onNeedChange }) => {
  const { data, loading } = useQuery(FOUNDERS_WHO_BRING, {
    variables: { where: whereBringing(need) },
  })
  const picked = pickFounders((data && data.premiumUsersLookingForCofounder) || [], bring)
  // keep the last cards up while the next skill loads, instead of a blank
  const lastShown = useRef([])
  if (picked.length) lastShown.current = picked
  const founders = picked.length ? picked : loading ? lastShown.current : []

  const [index, setIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [stopped, setStopped] = useState(false) // after a manual step, or reduced motion
  const paused = hovered || focused || stopped
  const count = founders.length

  useEffect(() => setIndex(0), [need, bring])
  useEffect(() => {
    if (prefersReducedMotion()) setStopped(true)
  }, [])
  useEffect(() => {
    if (paused || count < 2) return undefined
    const t = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS)
    return () => clearInterval(t)
  }, [paused, count])

  if (!count) return null
  const founder = founders[index % count]
  const step = (d) => {
    setStopped(true)
    setIndex((i) => (i + d + count) % count)
  }

  return (
    <section className="spotlight" aria-labelledby="spotlight-title">
      <div className="spotlight__copy">
        <h2 id="spotlight-title" className="spotlight__title">
          Founders who bring {skillById(need).bring}
        </h2>
        <p className="spotlight__text">
          Premium members are listed in the founder grid and featured here.
        </p>
        <div className="spotlight__line">
          <label htmlFor="spotlight-need">I need</label>
          <SkillSelect id="spotlight-need" value={need} onChange={onNeedChange} phrase="need" tone="blue" />
        </div>
        <Link href="/pricing">
          <a className="spotlight__link">See Premium</a>
        </Link>
      </div>
      <div
        className={`spotlight__card${loading && !picked.length ? " spotlight__card--stale" : ""}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <div key={founder.id} className="spotlight__slide" aria-live={paused ? "polite" : "off"}>
          <FounderCard founder={founder} bringId={bring} needId={need} />
        </div>
        {count > 1 && (
          <div className="spotlight__nav">
            <button type="button" className="spotlight__step" onClick={() => step(-1)} aria-label="Previous founder">
              <IconArrow flip />
            </button>
            <span className="spotlight__count">
              {(index % count) + 1} of {count}
            </span>
            <button type="button" className="spotlight__step" onClick={() => step(1)} aria-label="Next founder">
              <IconArrow />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

// Signed-out homepage: sell the outcome (a co-founder, then a plan), start
// matching from "I bring / I need", then show proof from the community.
const VisitorHome = () => {
  const [bring, setBring] = useState(DEFAULT_BRING)
  const [need, setNeed] = useState(DEFAULT_NEED)
  const bringKey = skillById(bring).key
  const needKey = skillById(need).key
  const startHref = `/welcome?bring=${bringKey}&need=${needKey}`
  const plan = SAMPLE_PLANS[needKey]
  const onStart = (where) =>
    event({ action: "home_match_start", category: "home", label: `${where}:${bringKey}>${needKey}` })

  return (
    <>
      <section className="visitor-hero" aria-labelledby="visitor-hero-title">
        <div className="visitor-hero__copy">
          <h1 id="visitor-hero-title" className="visitor-hero__title">
            Find your <span className="visitor-hero__nowrap">co-founder.</span> Then build
            together&nbsp;&mdash; with a plan.
          </h1>
          <p className="visitor-hero__lede">
            Tell us what you bring and who you need. Founders here have started
            26,000+ conversations with each other.
          </p>
          <div className="visitor-hero__chooser">
            <div className="visitor-hero__line">
              <label htmlFor="match-bring">I bring</label>
              <SkillSelect id="match-bring" value={bring} onChange={setBring} phrase="bring" tone="plain" />
            </div>
            <div className="visitor-hero__line">
              <label htmlFor="match-need">I need</label>
              <SkillSelect id="match-need" value={need} onChange={setNeed} phrase="need" tone="blue" />
            </div>
            <Link href={startHref}>
              <a className="visitor-hero__go" onClick={() => onStart("hero")}>
                Show my matches
              </a>
            </Link>
            <p className="visitor-hero__note">Free to join. A few more questions and we&rsquo;ll show you founders who fit.</p>
          </div>
        </div>
        <div className="visitor-hero__visual">
          <PlanCard plan={plan} />
        </div>
      </section>
      <PlanPreview plan={plan} href={startHref} onStart={onStart} />
      <FeaturedIdeas />
      <FounderSpotlight bring={bring} need={need} onNeedChange={setNeed} />
      <QuestionOfTheDay />
    </>
  )
}

export default VisitorHome
